// #region Imports

import type { IPlatformAdapter } from './Adapters/interfaces/IPlatformAdapter';
import { PlatformAdapterFactory } from './Adapters/PlatformAdapterFactory';
import type { IThemeGeneratorStrategy } from './Strategies/interfaces/IThemeGeneratorStrategy';
import { CosmopolitanThemeGeneratorStrategy } from './Strategies/surface/CosmopolitanThemeGeneratorStrategy';
import { JoyThemeGeneratorStrategy } from './Strategies/surface/JoyThemeGeneratorStrategy';
import { MemphisThemeGeneratorStrategy } from './Strategies/surface/MemphisThemeGeneratorStrategy';
import type { CssColor } from './Utils/CssColor';
import type { ThemeMode, ThemeModeWithSystem } from './Utils/ThemeMode';
import type { ThemePalette } from './Utils/ThemePalette';
import type { ThemeScheme } from './Utils/ThemeScheme';

// #endregion

/**
 * Central facade for generating theme palettes and color schemes.
 *
 * @remarks
 * Accepts any base color with any mode and autonomously ensures compatibility
 * before delegating to the resolved {@link IThemeGeneratorStrategy}.
 * The `'system'` mode is resolved to `'dark'` or `'light'` via the
 * {@link IPlatformAdapter}.
 *
 * @public
 */
export class ThemeGenerator {

    // #region Fields

    private readonly _strategies: Map<string, IThemeGeneratorStrategy>;
    private readonly _platformAdapter: IPlatformAdapter;

    // #endregion

    // #region Ctor

    /**
     * @public
     * @param platformAdapter - Optional platform adapter. Auto-detects if not provided.
     */
    public constructor(platformAdapter?: IPlatformAdapter) {
        this._strategies = new Map<string, IThemeGeneratorStrategy>([
            ['joy', new JoyThemeGeneratorStrategy()],
            ['memphis', new MemphisThemeGeneratorStrategy()],
            ['cosmopolitan', new CosmopolitanThemeGeneratorStrategy()]
        ]);
        this._platformAdapter = platformAdapter ?? PlatformAdapterFactory.create();
    }

    // #endregion

    // #region Methods

    /**
     * Generates a complete theme palette (shades 0–900) for the given theme.
     *
     * @remarks
     * 1. If `mode` is `'system'`, it is resolved to `'dark'` or `'light'` via the
     *    platform adapter's `prefers-color-scheme` media query.
     * 2. The base color is forwarded as-is to the theme-specific strategy which
     *    produces 11 shade steps from lightest (0) to darkest (900).
     *
     * No automatic color adaptation is performed. Callers who need mode-compatible
     * colors can use {@link IThemeGeneratorStrategy.isColorInModeSpectrum} and
     * {@link IThemeGeneratorStrategy.adaptColorToMode} explicitly before calling
     * this method.
     *
     * @public
     * @param themeName - The name of the theme (`'joy'`, `'memphis'`, `'cosmopolitan'`).
     * @param baseColor - The base accent color (any valid CSS color value).
     * @param mode - The target mode (`'dark'`, `'light'`, or `'system'`).
     * @returns A fully populated palette with shade keys `0`–`900`.
     * @throws Error if `themeName` is not a registered theme.
     */
    public generatePalette(themeName: string, baseColor: CssColor, mode: ThemeMode): Required<ThemePalette> {
        const resolvedMode = this.resolveMode(mode);
        const strategy = this.getStrategy(themeName);

        return strategy.generatePalette(baseColor, resolvedMode);
    }

    /**
     * Generates a semantic color scheme (surface, foreground, highlight, …) for the given theme.
     *
     * @remarks
     * 1. If `mode` is `'system'`, it is resolved to `'dark'` or `'light'` via the
     *    platform adapter's `prefers-color-scheme` media query.
     * 2. The base color is forwarded as-is to the theme-specific strategy which
     *    maps it to semantic roles such as `surface`, `foreground`, `highlight`,
     *    `contrast`, `disabled`, etc.
     *
     * No automatic color adaptation is performed. Callers who need mode-compatible
     * colors can use {@link IThemeGeneratorStrategy.isColorInModeSpectrum} and
     * {@link IThemeGeneratorStrategy.adaptColorToMode} explicitly before calling
     * this method.
     *
     * @public
     * @param themeName - The name of the theme (`'joy'`, `'memphis'`, `'cosmopolitan'`).
     * @param baseColor - The base background color (any valid CSS color value).
     * @param mode - The target mode (`'dark'`, `'light'`, or `'system'`).
     * @returns A fully populated scheme with all semantic color roles.
     * @throws Error if `themeName` is not a registered theme.
     */
    public generateScheme(themeName: string, baseColor: CssColor, mode: ThemeMode): Required<ThemeScheme> {
        const resolvedMode = this.resolveMode(mode);
        const strategy = this.getStrategy(themeName);

        return strategy.generateScheme(baseColor, resolvedMode);
    }

    /**
     * Resolves the theme mode, converting 'system' to 'dark' or 'light' based on platform settings.
     *
     * @private
     * @param mode The theme mode to resolve.
     * @returns The resolved theme mode.
     */
    private resolveMode(mode: ThemeModeWithSystem): ThemeMode {
        if (mode === 'system') {
            const isDark = this._platformAdapter.matchMedia('(prefers-color-scheme: dark)');
            return isDark ? 'dark' : 'light';
        }

        return mode;
    }

    /**
     * Gets the theme generator strategy for the specified theme name.
     *
     * @private
     * @param themeName The name of the theme ('joy', 'memphis', 'cosmopolitan').
     * @returns The theme generator strategy.
     */
    private getStrategy(themeName: string): IThemeGeneratorStrategy {
        const strategy = this._strategies.get(themeName);

        if (!strategy) {
            throw new Error(`Theme '${themeName}' is not supported.`);
        }

        return strategy;
    }

    // #endregion

}

/**
 * Global service locator for the singleton {@link ThemeGenerator} instance.
 *
 * @remarks
 * A default instance is created automatically at module load time.
 * Call {@link ThemeGeneratorServiceLocator.set | set} to replace it (e.g. in tests
 * or SSR environments where a custom {@link IPlatformAdapter} is needed).
 *
 * @public
 */
export class ThemeGeneratorServiceLocator {

    // #region Fields

    private static _current: ThemeGenerator | null = null;

    // #endregion

    // #region Properties

    public static get current(): ThemeGenerator {
        if (!ThemeGeneratorServiceLocator.isSet()) {
            throw new Error('\'ThemeGenerator\' not set.');
        }

        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        return ThemeGeneratorServiceLocator._current!;
    }

    // #endregion

    // #region Methods

    public static isSet(): boolean {
        return ThemeGeneratorServiceLocator._current !== null;
    }

    public static set(current: ThemeGenerator): void {
        ThemeGeneratorServiceLocator._current = current;
    }

    // #endregion

}

ThemeGeneratorServiceLocator.set(new ThemeGenerator());
